/**
 * Upload Configurations for Generic UploadModal
 * Each config defines column mappings, styling, and API endpoint
 */

export const UPLOAD_CONFIGS = {
  // =============================================================================
  // TARIFF UPLOAD
  // =============================================================================
  TARIFF: {
    title: 'Upload Tariff Data',
    icon: '💰',
    accentColor: '#17a2b8',
    bgLight: '#d1ecf1',
    textColor: '#0c5460',
    apiEndpoint: '/tariff/upload',
    columnMapping: {
      'ID': 'id',
      'Material Category': 'material_category',
      'Part Number': 'part_number',
      'Ottav Type': 'ottav_type',
      'HTS': 'hts',
      'COO': 'coo',
      'Country': 'country',
      'Mill': 'mill',
      'Exceptions': 'exceptions',
      'AD Case #': 'ad_case_number',
      'AD % (Nov 13)': 'ad_percent_nov_13',
      'AD %': 'ad_percent_nov_13',
      'CV Case #': 'cv_case_number',
      'CV % (Nov 13)': 'cv_percent_nov_13',
      'CV %': 'cv_percent_nov_13',
      'Section 232 % (Nov 13)': 'section_232_percent_nov_13',
      'Sec 232 %': 'section_232_percent_nov_13',
      'Section 301 % (Nov 13)': 'section_301_percent_nov_13',
      'Sec 301 %': 'section_301_percent_nov_13',
      'Reciprocal Tariffs % (Nov 13)': 'reciprocal_tariffs_percent_nov_13',
      'Reciprocal %': 'reciprocal_tariffs_percent_nov_13',
      'Max AD % per Country': 'max_ad_percent_per_country',
      'Max AD %': 'max_ad_percent_per_country',
      'Max CV % per Country': 'max_cv_percent_per_country',
      'Max CV %': 'max_cv_percent_per_country',
      'Max Section 232 % per Country': 'max_section_232_percent_per_country',
      'Max 232 %': 'max_section_232_percent_per_country',
      'Max Section 301 % per Country': 'max_section_301_percent_per_country',
      'Max 301 %': 'max_section_301_percent_per_country',
      'Max Reciprocal Tariffs % per Country': 'max_reciprocal_tariffs_percent_per_country',
      'Max Recip %': 'max_reciprocal_tariffs_percent_per_country',
    },
    numericColumns: [
      'ad_percent_nov_13', 'cv_percent_nov_13', 'section_232_percent_nov_13',
      'section_301_percent_nov_13', 'reciprocal_tariffs_percent_nov_13',
      'max_ad_percent_per_country', 'max_cv_percent_per_country',
      'max_section_232_percent_per_country', 'max_section_301_percent_per_country',
      'max_reciprocal_tariffs_percent_per_country'
    ],
    previewColumns: ['id', 'part_number', 'hts', 'country', 'mill'],
  },

  // =============================================================================
  // PURCHASING LIST UPLOAD
  // =============================================================================
  PURCHASING_LIST: {
    title: 'Upload Purchasing List',
    icon: '🛒',
    accentColor: '#28a745',
    bgLight: '#d4edda',
    textColor: '#155724',
    apiEndpoint: '/purchasing-list/upload',
    columnMapping: {
      'ID': 'id',
      'MTO Desc': 'mto_desc',
      'MTO Size': 'mto_size',
      'MTO Quan': 'mto_quan',
      'MTO Sche': 'mto_sche',
      'MTO Mate': 'mto_mate',
      'COO': 'coo',
      'AD %': 'ad_percent',
      'CV %': 'cv_percent',
      'Section 232 %': 'section_232_percent',
      'Section 301 %': 'section_301_percent',
      'Reciprocal %': 'reciprocal_percent',
      'Qty': 'qty',
      'Unit Price': 'unit_price',
      'Total Price Before Tariff': 'total_price_before_tariff',
      'Total Before Tariff': 'total_price_before_tariff',
      'Total Price After Tariff': 'total_price_after_tariff',
      'Total After Tariff': 'total_price_after_tariff',
    },
    numericColumns: [
      'mto_quan', 'ad_percent', 'cv_percent', 'section_232_percent',
      'section_301_percent', 'reciprocal_percent', 'qty', 'unit_price',
      'total_price_before_tariff', 'total_price_after_tariff'
    ],
    previewColumns: ['id', 'mto_desc', 'mto_size', 'qty', 'unit_price'],
  },

  // =============================================================================
  // MATERIAL LOG UPLOAD
  // =============================================================================
  MATERIAL_LOG: {
    title: 'Upload Material Log',
    icon: '📦',
    accentColor: '#fd7e14',
    bgLight: '#fff3cd',
    textColor: '#856404',
    apiEndpoint: '/material-management/upload',
    columnMapping: {
      'ID': 'id',
      'Part Number': 'part_number',
      'Mat Mngmnt Type': 'mat_mngmnt_type',
      'Ottav Type': 'ottav_type',
      'SCH': 'sch',
      'Material': 'mat',
      'Size': 'size',
      'Qty Needed for Job': 'qty_needed_for_job',
      'Qty in Stock': 'qty_in_stock',
      'Qty Received': 'qty_received',
      'Received By': 'received_by',
      'PO Number/FI': 'po_number_or_fi',
      'PO Qty': 'po_qty',
      'Packing Slip': 'packing_slip',
      'Priority': 'priority',
      'Comments': 'comments',
      'Received %': 'Received_%',
      'Location': 'location',
      'PO Date': 'po_date',
      'Received Date': 'received_date',
    },
    numericColumns: ['qty_needed_for_job', 'qty_in_stock', 'qty_received', 'po_qty', 'Received_%'],
    dateColumns: ['po_date', 'received_date'],
    previewColumns: ['id', 'part_number', 'mat_mngmnt_type', 'size', 'qty_received'],
  },

  // =============================================================================
  // MTO UPLOAD
  // =============================================================================
  MTO: {
    title: 'Upload MTO Data',
    icon: '📋',
    accentColor: '#6f42c1',
    bgLight: '#e2d9f3',
    textColor: '#5a32a3',
    apiEndpoint: '/mto/upload',
    // Plan 1: only RAW/manual input columns are mapped. Calculated columns (bore,
    // matbore, dia_in, total_joints, total_hours, *_cad/*_usd totals, mh_per_ea)
    // are derived server-side and intentionally NOT imported. Old headers are kept
    // as aliases so prior export files still import.
    columnMapping: {
      'ID': 'id',
      'Job Number': 'job_number', 'Job #': 'job_number',
      'Estimate Number': 'estimate_number', 'Estimate #': 'estimate_number',
      'Line Number': 'line_number', 'Line #': 'line_number',
      'ISO Sheet #': 'iso_sheet_number', 'ISO #': 'iso_sheet_number', 'Isometric Number': 'iso_sheet_number',
      'ISO Version': 'iso_version', 'ISO Rev': 'iso_version', 'Version': 'iso_version',
      'Spool Number': 'spool_number', 'Spool #': 'spool_number',
      'Part Number': 'part_number', 'Part #': 'part_number',
      // 'Description (rest)' is the grid/export label for the same field (ticket #17)
      'Description': 'description', 'Description (rest)': 'description',
      'Type': 'type', 'Material': 'material', 'Size': 'size',
      'SCH': 'sch', 'SCH Modified': 'sch_modified', 'Rating': 'rating',
      'End Type': 'end_type', 'Seam Type': 'seam_type', 'Seam': 'seam_type',
      'Joint Type': 'join_type', 'Join Type': 'join_type', 'JOIN TYPE': 'join_type',
      'Qty': 'qty', 'Joints': 'joints',
      'UM': 'um', 'Unit': 'um', 'UoM': 'um', 'UOM': 'um', 'Unit of Measure': 'um',
      'System': 'system', 'Subsystem': 'subsystem',
      'Piping Class': 'piping_class', 'Fluid Code': 'fluid_code',
      'SOV': 'sov', 'WBS': 'wbs', 'Length': 'length',
      'Fabrication Shop': 'fabrication_shop', 'Fab Shop': 'fabrication_shop', 'Priority': 'priority',
      'Class': 'class', 'UDF Class': 'class',
      'Restrictions': 'restrictions', 'UDF Restrictions': 'restrictions',
      'Support Code': 'support_code', 'UDF Support Code': 'support_code',
      'Pipe to Pipe Qty': 'pipe_to_pipe_qty', 'UDF Pipe to Pipe Qty': 'pipe_to_pipe_qty',
      'Unit Price CAD': 'unit_price_cad', 'Unit Price': 'unit_price_cad',
      'Labour Rate CAD': 'labour_rate_cad',
      'Supplier': 'supplier', 'Vendor': 'supplier',
      'Engineering Code': 'engineering_code', 'Eng Code': 'engineering_code', 'Eng. Code': 'engineering_code',
      'Purchasing Qty': 'purchasing_qty', 'Purchasing Quantity': 'purchasing_qty',
      'Trim Allowance': 'trim_allowance', 'Rev': 'rev', 'Comments': 'comments',
      'Oxygen Clean Factor': 'oxygen_clean_factor',
      'Field Length Factor': 'field_length_factor',
      'Shop Small Bore Factor': 'shop_small_bore_factor',
      'Field Small Bore Factor': 'field_small_bore_factor',
      'Support Factor': 'support_factor',
      'Sloped Pipe Factor': 'sloped_pipe_factor',
      'Price Cut to Pipe Factor': 'price_cut_to_pipe_factor', 'UDF Total Price Cut to Pipe': 'price_cut_to_pipe_factor',
      'Fitting to Fittings Factor': 'fitting_to_fittings_factor', 'UDF Total Price Fittings': 'fitting_to_fittings_factor',
      'Pipe to Pipe Factor': 'pipe_to_pipe_factor', 'UDF Total Price Pipe to Pipe': 'pipe_to_pipe_factor',
    },
    numericColumns: [
      'qty', 'purchasing_qty', 'joints', 'length', 'unit_price_cad', 'labour_rate_cad', 'trim_allowance', 'pipe_to_pipe_qty',
      'oxygen_clean_factor', 'field_length_factor', 'shop_small_bore_factor', 'field_small_bore_factor',
      'support_factor', 'sloped_pipe_factor', 'price_cut_to_pipe_factor', 'fitting_to_fittings_factor', 'pipe_to_pipe_factor',
    ],
    dateColumns: [],
    previewColumns: ['id', 'estimate_number', 'part_number', 'iso_sheet_number', 'iso_version', 'description', 'qty'],
    // Plan B: revision 3-ID model. Each upload creates a timestamped revision; the
    // user can tag it with the client's rev # and the transmittal #.
    extraFields: [
      {
        key: 'upload_type', label: 'Upload type', type: 'select', default: 'client',
        options: [
          { value: 'client', label: 'Excel from client (new revision)' },
          { value: 'iso_batch', label: 'ISO batch (append to job)' },
        ],
        help: 'Excel from client = a full snapshot → a new client revision (Bid, Rev 1, 2…). ISO batch = isometrics issued in batches over the job; appended to the bottom and consolidated for comparison (client rev is set to "multiple").',
      },
      { key: 'client_rev', label: 'Client Revision #', placeholder: 'e.g. Rev B, IFC, 02', help: 'Excel-from-client only — the revision number issued by the client / engineering firm. Ignored for ISO batches.' },
      { key: 'transmittal_number', label: 'Transmittal #', placeholder: 'e.g. TRX-00123', help: 'The transmittal this upload arrived on.' },
    ],
  },

  // =============================================================================
  // PART RATE DB UPLOAD (MCAA / OTT rates) — appends rows with auto part_number
  // =============================================================================
  PART_RATE_DB: {
    title: 'Import Part Rate DB',
    icon: '🗃️',
    accentColor: '#198754',
    bgLight: '#d1e7dd',
    textColor: '#0f5132',
    apiEndpoint: '/lt-part-rate-db/import',
    maxRows: 50000,
    instructions: 'Uploaded rows are appended as new parts; a 10-digit part number is assigned automatically.',
    columnMapping: {
      'Unique': 'unique_key', 'MCAA Location': 'mcaa_location', 'MATCHKEY': 'matchkey', 'Material': 'material',
      'Description': 'description', 'Sch': 'sch', 'SCH': 'sch', 'Rating': 'rating', 'End Type': 'end_type',
      'Seam Type': 'seam_type', 'Seam': 'seam_type', 'Joint Type': 'join_type', 'Join Type': 'join_type', 'JOIN TYPE': 'join_type', 'Size': 'size', 'OTT HOUR': 'ott_hour', 'OTT Hour': 'ott_hour',
      'MCAA_Rate': 'mcaa_rate', 'MCAA Rate': 'mcaa_rate', 'Part Number': 'part_number', 'Part #': 'part_number',
      'Cost CAD': 'cost_cad', 'Cost': 'cost_cad',
      'Joints': 'joints', 'JOINTS': 'joints',
      'Dia In': 'dia_in', 'DIA IN': 'dia_in',
    },
    // joints / dia_in must be numeric so inch strings like 1" parse to 1 (backend Number('1"') would null them)
    numericColumns: ['ott_hour', 'mcaa_rate', 'cost_cad', 'joints', 'dia_in'],
    dateColumns: [],
    previewColumns: ['material', 'description', 'size', 'sch', 'ott_hour', 'joints', 'dia_in'],
  },

  // =============================================================================
  // PART RATE DB — PURCHASER COST IMPORT (Plan 2)
  // Overwrites cost_cad (unit price) per part_number; appends to price history.
  // =============================================================================
  PART_RATE_COST: {
    title: 'Import Part Costs (CAD)',
    icon: '💲',
    accentColor: '#0d6efd',
    bgLight: '#cfe2ff',
    textColor: '#084298',
    apiEndpoint: '/lt-part-rate-db/import-cost',
    maxRows: 50000,
    instructions: 'Match rows by Part Number, or return the purchase-requisition Excel as-is (part numbers are read from the DESCRIPTION column). Each upload OVERWRITES the current unit cost (cost_cad), records price history, and updates matching MTO unit prices. If the same part appears with more than one cost, those parts are skipped and listed so you can fix them.',
    columnMapping: {
      'Part Number': 'part_number', 'Part #': 'part_number', 'Part': 'part_number',
      'Cost CAD': 'cost_cad', 'Cost': 'cost_cad', 'Unit Price': 'cost_cad', 'Unit Price CAD': 'cost_cad', 'Price': 'cost_cad',
      // Returned purchase-requisition format (QUANTITY | DESCRIPTION | PRICE | UNIT | TOTAL NET):
      // the part number is the leading token of the composite DESCRIPTION (extracted server-side).
      'Description': 'description',
    },
    numericColumns: ['cost_cad'],
    dateColumns: [],
    previewColumns: ['part_number', 'description', 'cost_cad'],
  },

  // =============================================================================
  // MATERIAL INDEX UPLOAD
  // =============================================================================
  MATERIAL_INDEX: {
    title: 'Import Material Index',
    icon: '📊',
    accentColor: '#fd7e14',
    bgLight: '#fff3e0',
    textColor: '#e65100',
    apiEndpoint: '/ottaviano/upload',
    instructions: 'To delete: Set "Action" column to "d"',
    columnMapping: {
      'Action': 'action', 'action': 'action',
      'ID': 'id', 'UID': 'uid', 'Category': 'category', 'Description 1': 'description_1', 'Description 2': 'description_2',
      'Long Description': 'long_description', 'Type': 'type_ottav', 'Type Ottav': 'type_ottav',
      'Type Part Number': 'type_part_number', 'Type Part #': 'type_part_number',
      'Part Number': 'part_number', 'Part #': 'part_number', 'Material': 'material',
      'Schedule Rating': 'schedule_rating', 'Schedule - Rating': 'schedule_rating',
      'Size': 'size', 'UM': 'um', 'Bore': 'bore', 'Mat1': 'mat1', 'Matbore': 'matbore',
      'Manufacturing Standard': 'manufacturing_standard', 'Design Code': 'design_code',
      'Seam Type': 'seam_type', 'End Type': 'end_type', 'External Coating': 'external_coating',
      'Bend Angle': 'bend_angle', 'Radius Type': 'radius_type', 'Connection Type': 'connection_type',
      'Facing Type': 'facing_type', 'Joint Weld To Be Reviewed': 'joint_weld_to_be_reviewed',
      'Joint/Weld Review': 'joint_weld_to_be_reviewed',
      'HR': 'hr', 'Dia In': 'dia_in', 'Material USD': 'material_usd', 'Material CAD': 'material_cad',
      'Labour Rate CAD': 'labour_rate_cad', 'Labour Rate USD': 'labour_rate_usd',
      'Labour Cost CAD': 'labour_cost_cad', 'Labour Cost USD': 'labour_cost_usd',
      'Ottav Unique': 'ottav_unique',
      'Rating': 'rating', 'Joints': 'joints', 'Dia 2': 'dia_2',
    },
    numericColumns: ['hr', 'dia_in', 'dia_2', 'material_usd', 'material_cad', 'labour_rate_cad', 'labour_rate_usd', 'labour_cost_cad', 'labour_cost_usd'],
    previewColumns: ['id', 'uid', 'category', 'description_1', 'hr'],
  },

  // =============================================================================
  // TRANSMITTAL UPLOAD
  // =============================================================================
  TRANSMITTAL: {
    title: 'Import Transmittal Log',
    icon: '📋',
    accentColor: '#343a40',
    bgLight: '#e9ecef',
    textColor: '#495057',
    apiEndpoint: '/transmittal/upload',
    columnMapping: {
      'Date': 'date', 'Job Number': 'job_number', 'Transmittal Number': 'transmittal_number',
      'Description': 'description', 'Type': 'type', 'Rev Number': 'rev_number',
      'Extra Number': 'extra_number', 'Comments': 'comments', 'Requested By': 'requested_by',
      'From': 'from_party', 'To': 'to_party', 'ISO Number': 'iso_number',
      'Drawing Number': 'drawing_number', 'Status': 'status', 'Next Submission Date': 'next_submission_date',
    },
    dateColumns: ['date', 'next_submission_date'],
    previewColumns: ['transmittal_number', 'job_number', 'description', 'type', 'status'],
  },

  // =============================================================================
  // HVAC TRACKING UPLOAD
  // =============================================================================
  HVAC: {
    title: 'Upload HVAC Tracking Data',
    icon: '❄️',
    accentColor: '#007bff',
    bgLight: '#e7f3ff',
    textColor: '#004085',
    apiEndpoint: '/discipline-matrix/hvac/upload-rows',
    columnMapping: {
      'ID': 'id',
      'Building Number': 'building_number', 'Building #': 'building_number',
      'Area': 'area', 'Sub Area': 'sub_area', 'Elevation': 'elevation', 'Floor': 'floor',
      'Job Number': 'job_number', 'Equipment Tag/Code': 'equipment_tag_or_code', 'Equipment Tag': 'equipment_tag_or_code',
      'Description': 'description', 'Budgeted Hr': 'budgeted_hr', 'Credit': 'credit',
      'Earned Hr': 'earned_hr', 'Earned Credit': 'earned_credit', 'Cost Code': 'cost_code',
      'SOV Code': 'sov_code', 'SOV Description': 'sov_description',
      'Delivery to Site %': 'delivery_to_site', 'Delivery %': 'delivery_to_site',
      'Rough Install %': 'rough_install', 'Supports %': 'supports', 'Accessories %': 'accessories',
      'Air Balancing %': 'air_balancing',
      'Total Field Completion %': 'total_field_completion', 'Total Field %': 'total_field_completion',
      'Drawing Number': 'drawing_number', 'Drawing #': 'drawing_number', 'P&ID': 'p_and_id',
      'Division Area': 'division_area', 'Sub Division Area': 'sub_division_area', 'Sub Division': 'sub_division_area',
    },
    numericColumns: [
      'budgeted_hr', 'credit', 'earned_hr', 'earned_credit',
      'delivery_to_site', 'rough_install', 'supports', 'accessories', 'air_balancing', 'total_field_completion'
    ],
    previewColumns: ['id', 'equipment_tag_or_code', 'area', 'description', 'total_field_completion'],
  },

  // =============================================================================
  // ROTATING EQUIPMENT TRACKING UPLOAD
  // =============================================================================
  ROTATING_EQUIPMENT: {
    title: 'Upload Rotating Equipment Data',
    icon: '⚙️',
    accentColor: '#e83e8c',
    bgLight: '#fce4ec',
    textColor: '#ad1457',
    apiEndpoint: '/discipline-matrix/mechanical/upload-rows',
    columnMapping: {
      'ID': 'id',
      'Building Number': 'building_number', 'Building #': 'building_number',
      'Area': 'area', 'Sub Area': 'sub_area', 'Elevation': 'elevation', 'Floor': 'floor',
      'Job Number': 'job_number', 'Equipment Tag/Code': 'equipment_tag_or_code', 'Equipment Tag': 'equipment_tag_or_code',
      'Description': 'description', 'Budgeted Hr': 'budgeted_hr', 'Credit': 'credit',
      'Earned Hr': 'earned_hr', 'Earned Credit': 'earned_credit', 'Cost Code': 'cost_code',
      'Check Base/Elev/Bolts %': 'check_base_elev_n_bolts', 'Check Base %': 'check_base_elev_n_bolts',
      'Prepare Concrete Base %': 'prepare_concrete_base', 'Prep Concrete %': 'prepare_concrete_base',
      'Rough Set %': 'rough_set',
      'Pre-Alignment %': 'pre_alignment', 'Pre-Alignment': 'pre_alignment',
      'Align & Level %': 'align_and_level', 'Align and Level %': 'align_and_level',
      'Torque Bolts %': 'torque_bolts', 'Install Rout %': 'install_rout',
      'Touch Up Painting %': 'touch_up_painting', 'Touch Up %': 'touch_up_painting',
      'Total Field Completion %': 'total_field_completion', 'Total Field %': 'total_field_completion',
      'Drawing Number': 'drawing_number', 'Drawing #': 'drawing_number', 'P&ID': 'p_and_id',
      'Division Area': 'division_area', 'Sub Division Area': 'sub_division_area', 'Sub Division': 'sub_division_area',
    },
    numericColumns: [
      'budgeted_hr', 'credit', 'earned_hr', 'earned_credit',
      'check_base_elev_n_bolts', 'prepare_concrete_base', 'rough_set', 'pre_alignment', 'align_and_level',
      'torque_bolts', 'install_rout', 'touch_up_painting', 'total_field_completion'
    ],
    previewColumns: ['id', 'equipment_tag_or_code', 'area', 'description', 'total_field_completion'],
  },

  // =============================================================================
  // VESSELS TRACKING UPLOAD
  // =============================================================================
  VESSELS: {
    title: 'Upload Vessels Tracking Data',
    icon: '🛢️',
    accentColor: '#6610f2',
    bgLight: '#ede7f6',
    textColor: '#4527a0',
    apiEndpoint: '/discipline-matrix/vessels/upload-rows',
    columnMapping: {
      'ID': 'id', 'Building Number': 'building_number', 'Building #': 'building_number', 'Area': 'area', 'Sub Area': 'sub_area',
      'Elevation': 'elevation', 'Floor': 'floor', 'Job Number': 'job_number', 'Equipment Tag/Code': 'equipment_tag_or_code',
      'Equipment Tag': 'equipment_tag_or_code', 'Description': 'description', 'Budgeted Hr': 'budgeted_hr', 'Credit': 'credit',
      'Earned Hr': 'earned_hr', 'Earned Credit': 'earned_credit', 'Cost Code': 'cost_code', 'Delivery to Site %': 'delivery_to_site',
      'Delivery %': 'delivery_to_site', 'Rough Set %': 'rough_set', 'Supports %': 'supports', 'Bolting %': 'bolting',
      'Accessories %': 'accessories', 'Grout %': 'grout', 'Total Field Completion %': 'total_field_completion',
      'Total Field %': 'total_field_completion', 'Drawing Number': 'drawing_number', 'Drawing #': 'drawing_number',
      'P&ID': 'p_and_id', 'Division Area': 'division_area', 'Sub Division Area': 'sub_division_area', 'Sub Division': 'sub_division_area',
    },
    numericColumns: [
      'budgeted_hr', 'credit', 'earned_hr', 'earned_credit',
      'delivery_to_site', 'rough_set', 'supports', 'bolting', 'accessories', 'grout', 'total_field_completion'
    ],
    previewColumns: ['id', 'equipment_tag_or_code', 'area', 'description', 'total_field_completion'],
  },

  // =============================================================================
  // STRUCTURAL STEEL TRACKING UPLOAD
  // =============================================================================
  STRUCTURAL_STEEL: {
    title: 'Upload Structural Steel Data',
    icon: '🏗️',
    accentColor: '#795548',
    bgLight: '#efebe9',
    textColor: '#4e342e',
    apiEndpoint: '/discipline-matrix/structural/upload-rows',
    columnMapping: {
      'ID': 'id', 'Building Number': 'building_number', 'Building #': 'building_number', 'Area': 'area', 'Sub Area': 'sub_area',
      'Elevation': 'elevation', 'Floor': 'floor', 'Job Number': 'job_number', 'Equipment Tag/Code': 'equipment_tag_or_code',
      'Equipment Tag': 'equipment_tag_or_code', 'Description': 'description', 'Budgeted Hr': 'budgeted_hr', 'Credit': 'credit',
      'Earned Hr': 'earned_hr', 'Earned Credit': 'earned_credit', 'Cost Code': 'cost_code',
      'SOV Code': 'sov_code', 'SOV Description': 'sov_description',
      'Receive & Shake Out %': 'receive_n_shake_out', 'Receive %': 'receive_n_shake_out',
      'Layout & Drill %': 'layout_n_drill', 'Layout %': 'layout_n_drill',
      'Erect %': 'erect', 'Bolt Up %': 'bolt_up', 'Paint Touch Up %': 'paint_touch_up', 'Paint %': 'paint_touch_up',
      'Total Field Completion %': 'total_field_completion', 'Total Field %': 'total_field_completion',
      'Drawing Number': 'drawing_number', 'Drawing #': 'drawing_number', 'P&ID': 'p_and_id',
      'Length': 'length', 'Weight': 'weight',
      'Division Area': 'division_area', 'Sub Division Area': 'sub_division_area', 'Sub Division': 'sub_division_area', 'Inter': 'inter',
    },
    numericColumns: [
      'budgeted_hr', 'credit', 'earned_hr', 'earned_credit',
      'receive_n_shake_out', 'layout_n_drill', 'erect', 'bolt_up', 'paint_touch_up', 'total_field_completion',
      'length', 'weight'
    ],
    previewColumns: ['id', 'equipment_tag_or_code', 'area', 'description', 'total_field_completion'],
  },

  // =============================================================================
  // DUCTS TRACKING UPLOAD
  // =============================================================================
  DUCTS: {
    title: 'Upload Ducts Tracking Data',
    icon: '🔩',
    accentColor: '#20c997',
    bgLight: '#e6fffa',
    textColor: '#0d7377',
    apiEndpoint: '/discipline-matrix/ducts/upload-rows',
    columnMapping: {
      'ID': 'id', 'Building Number': 'building_number', 'Building #': 'building_number',
      'Area': 'area', 'Sub Area': 'sub_area', 'Elevation': 'elevation', 'Floor': 'floor',
      'Job Number': 'job_number', 'Equipment Tag/Code': 'equipment_tag_or_code', 'Equipment Tag': 'equipment_tag_or_code',
      'Description': 'description', 'Budgeted Hr': 'budgeted_hr', 'Credit': 'credit',
      'Earned Hr': 'earned_hr', 'Earned Credit': 'earned_credit', 'Cost Code': 'cost_code',
      'SOV Code': 'sov_code', 'SOV Description': 'sov_description',
      'Delivery to Site %': 'delivery_to_site', 'Delivery %': 'delivery_to_site',
      'Rough Set %': 'rough_set', 'Supports %': 'supports', 'Bolting %': 'bolting',
      'Accessories %': 'accessories', 'Grout %': 'grout',
      'Total Field Completion %': 'total_field_completion', 'Total Field %': 'total_field_completion',
      'Drawing Number': 'drawing_number', 'Drawing #': 'drawing_number', 'P&ID': 'p_and_id',
      'Division Area': 'division_area', 'Sub Division Area': 'sub_division_area', 'Sub Division': 'sub_division_area',
    },
    numericColumns: [
      'budgeted_hr', 'credit', 'earned_hr', 'earned_credit',
      'delivery_to_site', 'rough_set', 'supports', 'bolting', 'accessories', 'grout', 'total_field_completion'
    ],
    previewColumns: ['id', 'equipment_tag_or_code', 'area', 'description', 'total_field_completion'],
  },

  // =============================================================================
  // CIVIL TRACKING UPLOAD
  // =============================================================================
  CIVIL: {
    title: 'Upload Civil Tracking Data',
    icon: '🧱',
    accentColor: '#8d6e63',
    bgLight: '#efebe9',
    textColor: '#4e342e',
    apiEndpoint: '/discipline-matrix/civil/upload-rows',
    columnMapping: {
      'ID': 'id', 'Building Number': 'building_number', 'Building #': 'building_number',
      'Area': 'area', 'Sub Area': 'sub_area', 'Elevation': 'elevation', 'Floor': 'floor',
      'Job Number': 'job_number', 'Equipment Tag/Code': 'equipment_tag_or_code', 'Equipment Tag': 'equipment_tag_or_code',
      'Description': 'description', 'Budgeted Hr': 'budgeted_hr', 'Credit': 'credit', 'Cost Code': 'cost_code',
      'SOV Code': 'sov_code', 'SOV Description': 'sov_description',
      'Site Prep %': 'site_prep', 'Excavation %': 'excavation', 'Formwork %': 'formwork',
      'Rebar %': 'rebar', 'Pour Concrete %': 'pour_concrete', 'Backfill %': 'backfill',
      'Drawing Number': 'drawing_number', 'Drawing #': 'drawing_number', 'P&ID': 'p_and_id',
      'Division Area': 'division_area', 'Sub Division Area': 'sub_division_area', 'Sub Division': 'sub_division_area',
      'Comments': 'comments',
      'UDF 01': 'udf01', 'UDF 02': 'udf02', 'UDF 03': 'udf03', 'UDF 04': 'udf04', 'UDF 05': 'udf05',
    },
    numericColumns: [
      'budgeted_hr', 'credit',
      'site_prep', 'excavation', 'formwork', 'rebar', 'pour_concrete', 'backfill'
    ],
    previewColumns: ['id', 'equipment_tag_or_code', 'area', 'description', 'budgeted_hr'],
  },

  // =============================================================================
  // BOILERMAKER TRACKING UPLOAD
  // =============================================================================
  BOILERMAKER: {
    title: 'Upload Boilermaker Tracking Data',
    icon: '🔥',
    accentColor: '#ef6c00',
    bgLight: '#fff3e0',
    textColor: '#e65100',
    apiEndpoint: '/discipline-matrix/boilermaker/upload-rows',
    columnMapping: {
      'ID': 'id', 'Building Number': 'building_number', 'Building #': 'building_number',
      'Area': 'area', 'Sub Area': 'sub_area', 'Elevation': 'elevation', 'Floor': 'floor',
      'Job Number': 'job_number', 'Equipment Tag/Code': 'equipment_tag_or_code', 'Equipment Tag': 'equipment_tag_or_code',
      'Description': 'description', 'Budgeted Hr': 'budgeted_hr', 'Credit': 'credit',
      'Earned Hr': 'earned_hr', 'Earned Credit': 'earned_credit', 'Cost Code': 'cost_code',
      'SOV Code': 'sov_code', 'SOV Description': 'sov_description',
      'Delivery to Site %': 'delivery_to_site', 'Delivery %': 'delivery_to_site',
      'Rough Set %': 'rough_set', 'Supports %': 'supports', 'Bolting %': 'bolting',
      'Accessories %': 'accessories', 'Grout %': 'grout',
      'Total Field Completion %': 'total_field_completion', 'Total Field %': 'total_field_completion',
      'Drawing Number': 'drawing_number', 'Drawing #': 'drawing_number', 'P&ID': 'p_and_id',
      'Division Area': 'division_area', 'Sub Division Area': 'sub_division_area', 'Sub Division': 'sub_division_area',
      'Comments': 'comments',
    },
    numericColumns: [
      'budgeted_hr', 'credit', 'earned_hr', 'earned_credit',
      'delivery_to_site', 'rough_set', 'supports', 'bolting', 'accessories', 'grout', 'total_field_completion'
    ],
    previewColumns: ['id', 'equipment_tag_or_code', 'area', 'description', 'budgeted_hr'],
  },

  // =============================================================================
  // ELECTRICAL TRACKING UPLOAD
  // =============================================================================
  ELECTRICAL: {
    title: 'Upload Electrical Tracking Data',
    icon: '⚡',
    accentColor: '#f9a825',
    bgLight: '#fff8e1',
    textColor: '#795500',
    apiEndpoint: '/discipline-matrix/electrical/upload-rows',
    columnMapping: {
      'ID': 'id', 'Building Number': 'building_number', 'Building #': 'building_number',
      'Area': 'area', 'Sub Area': 'sub_area', 'Elevation': 'elevation', 'Floor': 'floor',
      'Job Number': 'job_number', 'Equipment Tag/Code': 'equipment_tag_or_code', 'Equipment Tag': 'equipment_tag_or_code',
      'Description': 'description', 'Budgeted Hr': 'budgeted_hr', 'Credit': 'credit', 'Cost Code': 'cost_code',
      'SOV Code': 'sov_code', 'SOV Description': 'sov_description',
      'Conduit Install %': 'conduit_install', 'Cable Pull %': 'cable_pull', 'Termination %': 'termination',
      'Testing %': 'testing', 'Energize %': 'energize',
      'Drawing Number': 'drawing_number', 'Drawing #': 'drawing_number', 'P&ID': 'p_and_id',
      'Division Area': 'division_area', 'Sub Division Area': 'sub_division_area', 'Sub Division': 'sub_division_area',
      'Comments': 'comments',
      'UDF 01': 'udf01', 'UDF 02': 'udf02', 'UDF 03': 'udf03', 'UDF 04': 'udf04', 'UDF 05': 'udf05',
    },
    numericColumns: [
      'budgeted_hr', 'credit',
      'conduit_install', 'cable_pull', 'termination', 'testing', 'energize'
    ],
    previewColumns: ['id', 'equipment_tag_or_code', 'area', 'description', 'budgeted_hr'],
  },

  // =============================================================================
  // MECHANICAL UNIFIED UPLOAD (Rotating Equipment + Vessels + future types)
  // =============================================================================
  MECHANICAL_UNIFIED: {
    title: 'Upload Mechanical Data',
    icon: '⚙️',
    accentColor: '#2E7D32',
    bgLight: '#e8f5e9',
    textColor: '#1b5e20',
    apiEndpoint: '/mechanical/unified-upload/unified-upload',
    // Note: This config uses MechanicalUploadModal which handles Type-based routing
    // The columnMapping below is a combined mapping for validation/preview only
    columnMapping: {
      // Common columns
      'Type': 'type', 'type': 'type', 'TYPE': 'type',
      'Action': 'action', 'action': 'action',
      'ID': 'id',
      'Equipment Tag/Code': 'equipment_tag_or_code', 'Equipment Tag': 'equipment_tag_or_code', 'Tag': 'equipment_tag_or_code',
      'Description': 'description',
      'Area': 'area', 'Sub Area': 'sub_area',
      'Building Number': 'building_number', 'Building': 'building_number',
      'SOV Code': 'sov_code', 'WBS Code': 'wbs_code',
      'Floor': 'floor', 'Elevation': 'elevation',
      'Division Area': 'division_area', 'Sub Division Area': 'sub_division_area',
      'Job Number': 'job_number', 'Cost Code': 'cost_code',
      'Drawing Number': 'drawing_number', 'P&ID': 'p_and_id',
      'Budgeted Hr': 'budgeted_hr', 'Budgeted Hours': 'budgeted_hr', 'Budget Hours': 'budgeted_hr',
      'Credit': 'credit', 'Earned Hr': 'earned_hr', 'Earned Credit': 'earned_credit',
      'Shop Field Ratio': 'shop_field_ratio',
      'Date Equipment Received': 'date_equipment_received',
      'Equipment Receipt %': 'equipment_receipt_percent', 'Equipment Receipt/Requisitioned %': 'equipment_receipt_percent',
      'Comments': 'comments',
      // Rotating Equipment specific
      'Measure/Anchor/Sole Plate %': 'measure_anchor_sole_plate',
      'Measure, Anchor, Bush Hammer, Sole Plate Install %': 'measure_anchor_sole_plate',
      'Prepare Concrete Base': 'prepare_concrete_base', 'Prepare Concrete Base %': 'prepare_concrete_base',
      'Rough Set': 'rough_set', 'Rough Set %': 'rough_set',
      'Pre-Alignment': 'pre_alignment', 'Pre-Alignment %': 'pre_alignment',
      'Pre Alignment': 'pre_alignment', 'Pre Alignment %': 'pre_alignment',
      'Final align /level %': 'align_and_level', 'Final Align/Level %': 'align_and_level',
      'Align & Level': 'align_and_level', 'Align and Level': 'align_and_level',
      'Torque Bolts': 'torque_bolts', 'Torque Bolts %': 'torque_bolts',
      'Grout Stage 1 & 2 %': 'grout_stage', 'Grout Stage': 'grout_stage', 'Install Grout': 'grout_stage',
      'Punchlist/Commissioning %': 'punchlist_commissioning', 'Punchlist Commissioning': 'punchlist_commissioning',
      // Vessels specific
      'Delivery to Site %': 'delivery_to_site', 'Delivery to Site': 'delivery_to_site',
      'Supports %': 'supports', 'Supports': 'supports',
      'Bolting %': 'bolting', 'Bolting': 'bolting',
      'Accessories %': 'accessories', 'Accessories': 'accessories',
      'Grout %': 'grout', 'Grout': 'grout',
      'Measure/Anchor/Bush Hammer %': 'measure_anchor_bush_hammer',
      'Vessel Close Out %': 'vessel_close_out', 'Vessel Close Out': 'vessel_close_out',
      'Insulation %': 'insulation', 'Insulation': 'insulation',
      // Common completion
      'Total Field Completion %': 'total_field_completion',
      // UDF columns
      'UDF01': 'udf01', 'UDF02': 'udf02', 'UDF03': 'udf03', 'UDF04': 'udf04', 'UDF05': 'udf05',
      'UDF06': 'udf06', 'UDF07': 'udf07', 'UDF08': 'udf08', 'UDF09': 'udf09', 'UDF10': 'udf10',
    },
    numericColumns: [
      'budgeted_hr', 'credit', 'earned_hr', 'earned_credit',
      'equipment_receipt_percent', 'measure_anchor_sole_plate', 'prepare_concrete_base',
      'rough_set', 'pre_alignment', 'align_and_level', 'torque_bolts', 'grout_stage', 'punchlist_commissioning',
      'delivery_to_site', 'supports', 'bolting', 'accessories', 'grout',
      'measure_anchor_bush_hammer', 'vessel_close_out', 'insulation', 'total_field_completion'
    ],
    previewColumns: ['type', 'equipment_tag_or_code', 'area', 'description'],
    // Flag indicating this uses the specialized MechanicalUploadModal
    useSpecializedModal: true,
    validTypes: ['Rotating', 'Vessels'], // Will be populated dynamically
  },
};

export default UPLOAD_CONFIGS;

